// ============================================================================
// THE JOURNAL DRAFT — the one copy of today's words that survives the phone.
//
// WHY THIS EXISTS. Until this file, unsaved journal text lived in exactly two
// places, and neither of them survives anything: the DOM textarea, and the
// in-memory TanStack cache. So a failed save on the phone lost the words the
// moment the tab was swiped out of recents, the session expired, or the OS
// reclaimed the app — and the composer's own promise ("Not saved — still here,
// retries as you type") was only true for as long as the tab lived.
//
// WHY localStorage AND NOT IndexedDB. The write has to happen on the way out —
// inside `pagehide`, inside a keystroke handler the OS may never come back
// from. `localStorage.setItem` is SYNCHRONOUS and therefore already durable
// when it returns. An IndexedDB transaction is a promise, and a promise that
// needs one more turn of the event loop is a promise the OS can cancel. Size is
// not the constraint here (a day of writing is kilobytes); the moment of the
// write is.
//
// SHAPE. One key per DAY — `lifeos.journal.draft.<date>` — namespaced like
// `lifeos.scale` and `lifeos.journal.dailyPrompt`. Per-day rather than one
// blob, because a flush can land after midnight and two days' words must never
// share a slot.
//
// Same shape as scale.ts and journal-prefs.ts on purpose: pure functions that
// take their storage as an argument, so every rule below is testable without a
// browser (journal-draft.test.ts). Every access is wrapped — `localStorage`
// itself throws in a private window or a locked-down profile, and a composer
// that refuses to accept a keystroke because storage is off would be a worse
// app than one that quietly keeps less.
// ============================================================================

import type { ISODate } from '@app/shared'

export interface JournalDraft {
  /** The day these words belong to. A flush after midnight must not land on yesterday. */
  date: ISODate
  /** Exactly what was in the box at the last keystroke. */
  text: string
  /** When that keystroke happened, by the real wall clock. Rendered 12-hour by the composer. */
  typedAt: string
  /**
   * What the SERVER was last known to hold for this day when the typing started.
   *
   * It is not decoration: it is the only way the composer can tell "these words
   * grew out of what is saved" from "these words were typed while the entry
   * could not be read", and those two need different sentences. Sending the
   * second kind replaces whatever is on the server, and the app has to say so
   * rather than find out afterwards.
   */
  lastConfirmedText: string
}

/** Namespaced like THEME_STORAGE_KEY / SCALE_STORAGE_KEY, one key per day. */
export const JOURNAL_DRAFT_KEY_PREFIX = 'lifeos.journal.draft.'

export function journalDraftKey(date: ISODate): string {
  return `${JOURNAL_DRAFT_KEY_PREFIX}${date}`
}

type ReadStore = Pick<Storage, 'getItem'> | null | undefined
type WriteStore = Pick<Storage, 'setItem'> | null | undefined
type ClearStore = Pick<Storage, 'removeItem'> | null | undefined

/**
 * The browser's own store, or null. Reading the PROPERTY throws in a locked-down
 * profile — not just reading a key — so even this is guarded.
 */
export function journalDraftStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

/**
 * The draft for a day, or null when there is none.
 *
 * A value that does not parse, carries no text, or names a different day is
 * treated as absent rather than repaired. A draft is the last thing in the app
 * allowed to guess: half a draft put into the composer would be words the owner
 * never wrote.
 */
export function readJournalDraft(storage: ReadStore, date: ISODate): JournalDraft | null {
  try {
    const raw = storage?.getItem(journalDraftKey(date))
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    const draft = parsed as Record<string, unknown>
    if (typeof draft.text !== 'string') return null
    if (draft.date !== date) return null
    return {
      date,
      text: draft.text,
      typedAt: typeof draft.typedAt === 'string' ? draft.typedAt : '',
      lastConfirmedText:
        typeof draft.lastConfirmedText === 'string' ? draft.lastConfirmedText : '',
    }
  } catch {
    return null
  }
}

/**
 * Persist the words. Called on EVERY keystroke, before anything that can fail.
 *
 * It RETURNS whether the words actually landed, and the composer uses that answer rather than
 * assuming: a private window refuses storage, and a chip promising "kept on this phone" over a
 * refusal would be the app fabricating a guarantee it does not have.
 */
export function writeJournalDraft(storage: WriteStore, draft: JournalDraft): boolean {
  try {
    storage?.setItem(journalDraftKey(draft.date), JSON.stringify(draft))
    return storage != null
  } catch {
    // A browser that refuses storage still gets a working composer for this session — the
    // words are in the box and the save still runs. Refusing the keystroke would be worse.
    return false
  }
}

/** Forget the words. Only ever called once the server has confirmed them. */
export function clearJournalDraft(storage: ClearStore, date: ISODate): void {
  try {
    storage?.removeItem(journalDraftKey(date))
  } catch {
    // Same reasoning as the write: a storage that cannot be cleared is not a reason to throw
    // on a successful save. The stale draft is compared against the server text on the next
    // mount, so the worst case is a chip that under-claims, never one that over-claims.
  }
}

/**
 * THE ONLY WAY A DRAFT IS ALLOWED TO DISAPPEAR: the server came back and said it
 * holds exactly these words.
 *
 * Deliberately NOT "a save succeeded" — that is a flag, and a flag cannot tell
 * the difference between the save that confirmed this text and the one that
 * confirmed the text from three keystrokes ago while the owner kept typing. The
 * comparison is the whole point. Returns whether it actually cleared.
 */
export function confirmJournalDraft(
  storage: (ReadStore & ClearStore) | null | undefined,
  date: ISODate,
  confirmedText: string,
): boolean {
  const draft = readJournalDraft(storage, date)
  if (!draft) return false
  if (draft.text !== confirmedText) return false
  clearJournalDraft(storage, date)
  return true
}

/**
 * What the composer should put back into the box, or null for "nothing to do".
 *
 * A draft whose words the server already has is not a restore — it is the same
 * sentence twice. `serverText` being undefined (the read has not landed, or it
 * failed) means the server's words are UNKNOWN, and an unknown is never a
 * reason to drop a draft: it restores.
 */
export function draftToRestore(
  draft: JournalDraft | null,
  serverText: string | null | undefined,
): JournalDraft | null {
  if (!draft) return null
  if (typeof serverText === 'string' && draft.text === serverText) return null
  return draft
}

/**
 * Would sending this draft REPLACE words the server has that the draft never
 * saw? True when the entry moved on — another device wrote it, or the draft was
 * typed while the entry could not be read at all.
 *
 * The composer says this out loud next to the Retry button rather than
 * discovering it after the PUT.
 */
export function draftWouldReplace(
  draft: JournalDraft | null,
  serverText: string | null | undefined,
): boolean {
  if (!draft) return false
  if (typeof serverText !== 'string') return false
  if (serverText.length === 0) return false
  return draft.lastConfirmedText !== serverText
}
